import type {
  AiBulkRequestCard,
  AiConversationMessage,
  AiMisuseCard,
  AiOwnedSpotCard,
  AiUsageCard,
  AiWindowCard,
} from '../../api/ai';
import type { AvailableSpot } from '../../api/available-spots';

export interface AssistantChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  spots?: AvailableSpot[];
  usages?: AiUsageCard[];
  ownedSpots?: AiOwnedSpotCard[];
  windows?: AiWindowCard[];
  bulkRequests?: AiBulkRequestCard[];
  misuseReports?: AiMisuseCard[];
  suggestBulkRequest?: boolean;
  suggestReportMisuse?: boolean;
}

export function stripChatMarkdown(text: string): string {
  return text
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/`([^`]+)`/g, '$1');
}

export function availabilityIntroFromMessage(content: string): string {
  const cleaned = stripChatMarkdown(content)
    .replace(/\n*você gostaria de reservar[^\n]*$/i, '')
    .replace(/\n*quer(?:ia)? reservar[^\n]*$/i, '')
    .trim();

  const beforeList = cleaned.split(/\n\s*[-*•]/)[0]?.trim() ?? cleaned;
  const firstParagraph = beforeList.split(/\n{2,}/)[0]?.trim() ?? beforeList;
  const sentences = firstParagraph.split(/(?<=[.!?])\s+/).filter(Boolean);
  const intro = (sentences.slice(0, 2).join(' ') || firstParagraph).trim();
  return stripInlinePeriodDump(intro);
}

function stripInlinePeriodDump(text: string): string {
  return text
    .replace(/\s+no período de[\s\S]*$/i, '.')
    .replace(/\s+de \d{2}\/\d{2}(?:\/\d{4})?,?\s*\d{2}:\d{2}[\s\S]*$/i, '.')
    .replace(/\s+\.$/, '.')
    .replace(/\.\.$/, '.')
    .trim();
}

export function assistantChatMessagesFromApi(
  messages: AiConversationMessage[],
): AssistantChatMessage[] {
  return messages.flatMap((message) => {
    if (message.role !== 'USER' && message.role !== 'ASSISTANT') {
      return [];
    }
    return [
      {
        id: message.id,
        role: message.role === 'USER' ? 'user' : 'assistant',
        content: message.content,
        ...assistantAttachmentsFromMetadata(message.metadata),
      } satisfies AssistantChatMessage,
    ];
  });
}

export function assistantAttachmentsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): Omit<AssistantChatMessage, 'id' | 'role' | 'content'> {
  return {
    spots: spotCardsFromMetadata(metadata),
    usages: usageCardsFromMetadata(metadata),
    ownedSpots: ownedSpotCardsFromMetadata(metadata),
    windows: windowCardsFromMetadata(metadata),
    bulkRequests: bulkRequestCardsFromMetadata(metadata),
    misuseReports: misuseCardsFromMetadata(metadata),
    ...(metadata?.suggestBulkRequest === true ? { suggestBulkRequest: true } : {}),
    ...(metadata?.suggestReportMisuse === true ? { suggestReportMisuse: true } : {}),
  };
}

export function spotCardsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): AvailableSpot[] | undefined {
  const raw = metadata?.spotCards;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.filter(isAvailableSpot);
}

export function usageCardsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): AiUsageCard[] | undefined {
  const raw = metadata?.usageCards;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.filter(isUsageCard);
}

export function ownedSpotCardsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): AiOwnedSpotCard[] | undefined {
  const raw = metadata?.ownedSpotCards;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.filter(isOwnedSpotCard);
}

export function windowCardsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): AiWindowCard[] | undefined {
  const raw = metadata?.windowCards;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.filter(isWindowCard);
}

export function bulkRequestCardsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): AiBulkRequestCard[] | undefined {
  const raw = metadata?.bulkRequestCards;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.filter(isBulkRequestCard);
}

export function misuseCardsFromMetadata(
  metadata: Record<string, unknown> | null | undefined,
): AiMisuseCard[] | undefined {
  const raw = metadata?.misuseCards;
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw.filter(isMisuseCard);
}

function isAvailableSpot(value: unknown): value is AvailableSpot {
  if (!value || typeof value !== 'object') return false;
  const spot = value as Partial<AvailableSpot>;
  return typeof spot.id === 'string' && typeof spot.code === 'string';
}

function isUsageCard(value: unknown): value is AiUsageCard {
  if (!value || typeof value !== 'object') return false;
  const usage = value as Partial<AiUsageCard>;
  return (
    typeof usage.id === 'string' &&
    typeof usage.spotId === 'string' &&
    typeof usage.spotCode === 'string' &&
    typeof usage.startAt === 'string' &&
    typeof usage.endAt === 'string' &&
    (usage.type === 'BORROW' || usage.type === 'RENT') &&
    typeof usage.status === 'string' &&
    typeof usage.canCancel === 'boolean'
  );
}

function isOwnedSpotCard(value: unknown): value is AiOwnedSpotCard {
  if (!value || typeof value !== 'object') return false;
  const spot = value as Partial<AiOwnedSpotCard>;
  return (
    typeof spot.id === 'string' &&
    typeof spot.spotId === 'string' &&
    typeof spot.spotCode === 'string' &&
    typeof spot.canLend === 'boolean'
  );
}

function isWindowCard(value: unknown): value is AiWindowCard {
  if (!value || typeof value !== 'object') return false;
  const window = value as Partial<AiWindowCard>;
  return (
    typeof window.id === 'string' &&
    typeof window.spotId === 'string' &&
    typeof window.spotCode === 'string' &&
    typeof window.startAt === 'string' &&
    typeof window.endAt === 'string' &&
    typeof window.canCancel === 'boolean'
  );
}

function isBulkRequestCard(value: unknown): value is AiBulkRequestCard {
  if (!value || typeof value !== 'object') return false;
  const request = value as Partial<AiBulkRequestCard>;
  return (
    typeof request.id === 'string' &&
    typeof request.startAt === 'string' &&
    typeof request.endAt === 'string' &&
    typeof request.expiresAt === 'string' &&
    (request.type === 'BORROW' || request.type === 'RENT') &&
    typeof request.canCancel === 'boolean'
  );
}

function isMisuseCard(value: unknown): value is AiMisuseCard {
  if (!value || typeof value !== 'object') return false;
  const report = value as Partial<AiMisuseCard>;
  return (
    typeof report.id === 'string' &&
    typeof report.spotId === 'string' &&
    typeof report.spotCode === 'string' &&
    typeof report.licensePlate === 'string' &&
    typeof report.createdAt === 'string' &&
    typeof report.canResolve === 'boolean'
  );
}

export function assistantUsageCanCancel(
  usage: AiUsageCard,
  liveUsages: Array<{ id: string; status: string }>,
  cancelledIds: ReadonlySet<string>,
): boolean {
  if (usage.role === 'owner') return false;
  if (cancelledIds.has(usage.id)) return false;
  const live = liveUsages.find((item) => item.id === usage.id);
  if (live) {
    return live.status === 'PENDING' || live.status === 'ACTIVE';
  }
  return usage.canCancel;
}

export function assistantUsageCanApprove(
  usage: AiUsageCard,
  decidedIds: ReadonlySet<string>,
): boolean {
  if (decidedIds.has(usage.id)) return false;
  if (usage.role === 'owner') return usage.canApprove === true;
  return false;
}

export function assistantUsageCanReject(
  usage: AiUsageCard,
  decidedIds: ReadonlySet<string>,
): boolean {
  if (decidedIds.has(usage.id)) return false;
  if (usage.role === 'owner') return usage.canReject === true;
  return false;
}
